import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { RouterProps, useNavigate, useParams, Link } from 'react-router-dom'
import logo from '../logo.svg'
import './HistoricalVersion.scss'
import { Helmet } from 'react-helmet-async'
import SideMenu from '../components/SideMenu'
import store, {
  RootState,
  AppDispatch,
  useAppDispatch,
  notesSlice,
  methods,
  reducer,
} from '../store'
import { useSelector, useDispatch } from 'react-redux'
import { bindEvent } from '../modules/bindEvent'
import { useTranslation } from 'react-i18next'
import { snackbar, prompt, alert, progressBar } from '@saki-ui/core'
import { AES, Debounce, deepCopy, images } from '@nyanyajs/utils'

import { Header } from '../components'
import { api } from '../modules/electron/api'
import { CategoryItem, NoteItem, PageItem } from '../store/notes/typings'
import { ReaderRouterProps } from '../modules/renderRoutes'
import { resolve } from 'path'
import { rejects } from 'assert'
import { SyncOff } from './Icon'
import {
  configSlice,
  eventListener,
  initialConfigState,
  saass,
} from '../store/config'
import { IContext } from '../store'
import { log } from 'console'
import { decryptContent, encryptContent } from '../modules/methods'
import { protoRoot } from '../protos'
import { config, pid } from 'process'
import {
  getImportanceColor,
  getSubTodoCount,
  getSubTodoIds,
  getTodoList,
} from '../store/notes/todolist'
import moment from 'moment'
import { ContentResult, getContent } from '../store/notes/methods'

import { ImportanceDropdownComponent } from './TodoListContentPage'
import { click } from '@testing-library/user-event/dist/click'
import { catalogSlice, getCatalogEl } from '../store/catalog'
import {
  SakiAvatar,
  SakiButton,
  SakiCheckbox,
  SakiCheckboxItem,
  SakiIcon,
  SakiModal,
  SakiModalHeader,
  SakiRow,
  SakiScrollLoading,
  SakiTitle,
  SakiBreadcrumb,
  SakiCascaderDropdown,
} from './saki-ui-react/components'
import { httpApi } from '../modules/http/api'
import { BreadcrumbOptionItem } from './saki-ui-react/types/components/Breadcrumb/breadcrumb'

const HistoricalVersionPage = ({
  contentResult,
}: {
  contentResult: ContentResult
}) => {
  const { t, i18n } = useTranslation('historicalVersionPage')
  const dispatch = useDispatch<AppDispatch>()
  const notes = useSelector((state: RootState) => state.notes)
  const config = useSelector((state: RootState) => state.config)

  const [selectVersionId, setSelectVersionId] = useState<string>('')

  const loadStatus = useRef('loaded')
  const pageSize = useRef(10)

  const [pageNum, setPageNum] = useState(1)
  const [breadcrumbOptions, setBreadcrumbOptions] = useState<
    BreadcrumbOptionItem[]
  >([])
  const [restoreToValues, setRestoreToValues] = useState<string[]>([])

  const asideContentModal = useSelector(
    (state: RootState) => state.config.pageConfig.asideContentModal
  )

  useEffect(() => {
    eventListener.dispatch(
      'confirmButtonDisabled',
      !(selectVersionId !== '' && selectVersionId !== 'currentVersion')
    )
    eventListener.on('restoreButtonClick', async () => {
      alert({
        title: t('restoreHistoricalVersion', {
          ns: 'historicalVersionPage',
        }),
        content: t('restoreHistoricalVersionContent', {
          ns: 'historicalVersionPage',
        }),
        cancelText: t('cancel', {
          ns: 'common',
        }),
        confirmText: t('confirm', {
          ns: 'sakiuiPrompt',
        }),
        onCancel() {},
        async onConfirm() {
          console.log('restoreButtonClick', selectVersionId, asideContentModal)
          const res = await httpApi.v1.getHistoricalVersionNote({
            versionId: selectVersionId,
            noteId: restoreToValues[0] || '',
            categoryId: restoreToValues[1] || '',
            pageId: restoreToValues[2] || '',
          })
          console.log('getHistoricalVersionNote', res, restoreToValues)
          if (res.code === 200) {
            if (res?.data?.note) {
              dispatch(
                notesSlice.actions.updateNote({
                  noteId: restoreToValues[0] || '',
                  note: {
                    name: res?.data?.note?.name || '',
                    categories: res?.data?.note?.categories || undefined,
                  },
                })
              )
            }
            if (res?.data?.category) {
              dispatch(
                notesSlice.actions.updateCategory({
                  noteId: restoreToValues[0] || '',
                  categoryId: restoreToValues[1] || '',
                  category: {
                    name: res?.data?.category?.name || '',
                    data: res?.data?.category?.data || [],
                  },
                })
              )
            }
            if (res?.data?.page) {
              dispatch(
                notesSlice.actions.updatePage({
                  noteId: restoreToValues[0] || '',
                  categoryId: restoreToValues[1] || '',
                  pageId: restoreToValues[2] || '',
                  data: {
                    title: res?.data?.page?.title || '',
                    content: res?.data?.page?.content || '',
                    encryption: (res?.data?.page?.encryption as any) || -1,
                    // type: (res?.data?.page?.type as any) || undefined,
                  },
                })
              )
            }
          }
        },
      }).open()
    })
    return () => {
      eventListener.removeEvent('restoreButtonClick')
    }
  }, [selectVersionId, restoreToValues])

  const d = useRef(new Debounce())
  useEffect(() => {
    if (
      asideContentModal.visible &&
      asideContentModal.data?.noteId &&
      asideContentModal.data?.type === 'HistoricalVersion' &&
      pageNum === 1
    ) {
      d.current.increase(() => {
        console.log(
          'getHistoricalVersionNoteUrls restoreToValues',
          restoreToValues,
          contentResult,
          pageNum
        )
        getHistoryFiles(pageNum)
      }, 100)
    }
  }, [asideContentModal.visible, pageNum])

  useMemo(() => {
    const breadcrumbOptions: BreadcrumbOptionItem[] = []
    if (contentResult.note) {
      breadcrumbOptions.push({
        value: contentResult.note.id || '',
        text: contentResult.note.name || '',
      })
    }
    if (contentResult.category) {
      breadcrumbOptions.push({
        value: contentResult.category.id || '',
        text: contentResult.category.name || '',
      })
    }
    if (contentResult.page) {
      breadcrumbOptions.push({
        value: contentResult.page.id || '',
        text:
          contentResult?.page?.title ||
          t('untitledPage', {
            ns: 'indexPage',
          }),
      })
    }
    setBreadcrumbOptions(breadcrumbOptions)
    setRestoreToValues(
      Object.keys(contentResult)
        .filter((v) => {
          if (v === 'todoList') {
            return false
          }
          return (contentResult as any)[v]?.id
        })
        .map((v) => {
          return (contentResult as any)[v]?.id
        })
    )
  }, [contentResult])

  const [historyFiles, setHistoryFiles] = useState<
    protoRoot.sync.GetHistoricalVersionNoteUrls.Response.IStaticFileUrlsItem[]
  >([])

  const getHistoryFiles = async (pageNum: number) => {
    if (loadStatus.current === 'loading' || loadStatus.current === 'noMore')
      return
    loadStatus.current = 'loading'
    const t = Math.floor(new Date().getTime() / 1000)
    const res = await httpApi.v1.getHistoricalVersionNoteUrls({
      // noteId: contentResult.note?.id || '',
      // categoryId: contentResult.category?.id || '',
      // pageId: contentResult.page?.id || '',
      noteId: restoreToValues[0] || '',
      categoryId: restoreToValues[1] || '',
      pageId: restoreToValues[2] || '',
      pageNum: pageNum,
      pageSize: pageSize.current,
      timeLimit: [t - 3600 * 24 * 30, t],
    })
    console.log(
      'getHistoricalVersionNoteUrls',
      res,
      res?.data?.total === 0,
      pageNum,
      {
        noteId: restoreToValues[0] || '',
        categoryId: restoreToValues[1] || '',
        pageId: restoreToValues[2] || '',
        pageNum: pageNum,
        pageSize: pageSize.current,
        timeLimit: [t - 3600 * 24 * 30, t],
      }
    )
    if (res?.code === 200 && res?.data) {
      setHistoryFiles(historyFiles.concat(res?.data?.list || []))
    }
    loadStatus.current =
      res?.data?.total && res?.data?.total > 0 ? 'loaded' : 'noMore'
    setPageNum(pageNum + 1)
  }
  return (
    <div className="historical-version-page">
      <div className={'hv-main ' + 'scrollBarHover'}>
        {/* <SakiBreadcrumb
					padding='0 20px'
					options={breadcrumbOptions}
				></SakiBreadcrumb> */}

        <SakiCascaderDropdown
          ref={
            bindEvent({
              close: () => {
                console.log('SakiCascaderDropdown close')
                setHistoryFiles([])
                setPageNum(1)
              },
            }) as any
          }
          onChangevalue={async (e) => {
            console.log('saki-cascader-dropdown', e)

            await e.target.buttonTextRender(
              e.detail.values?.length
                ? t('restoreTo', {
                    data: e.detail.values.map((v) => v.text).join(' > '),
                  })
                : ''
            )
            setRestoreToValues(e.detail.values.map((v) => v.value))
          }}
          options={notes.list.map((v) => {
            return {
              value: v.id || '',
              text: v.name || '',
              list: v.categories?.map((sv) => {
                return {
                  value: sv.id || '',
                  text: sv.name || '',
                  list: sv.data?.map((sv) => {
                    return {
                      value: sv.id || '',
                      text:
                        sv.title ||
                        t('untitledPage', {
                          ns: 'indexPage',
                        }) ||
                        '',
                    }
                  }),
                }
              }),
            }
          })}
          values={restoreToValues}
          placeholder={t('selectRestore')}
          width="calc(100% - 20px)"
          margin="0 20px"
          itemWidth={config.deviceType === 'Mobile' ? '100px' : '160px'}
          itemFontSize={config.deviceType === 'Mobile' ? '13px' : '14px'}
          changeOnSelect
        ></SakiCascaderDropdown>
        <div className="hv-m-list">
          <SakiCheckbox
            onSelectvalue={(e) => {
              console.log(e)

              setSelectVersionId(e.detail.value)
            }}
            value={selectVersionId}
            flex-direction="Column"
            type="Radio"
          >
            {historyFiles.map((v, i) => {
              return (
                <SakiCheckboxItem
                  key={v.id}
                  margin="0 0px 0 20px"
                  value={i === 0 ? 'currentVersion' : v.id || ''}
                  contentWidth="100%"
                  // disabled={i === 0}
                >
                  <div className="hv-m-l-item">
                    <div className="item-left">
                      <div className="item-l-top">
                        <span>
                          {moment((v?.createTime || 0) * 1000).calendar({
                            sameDay:
                              '[' +
                              t('today', {
                                ns: 'sakiuiWeather',
                              }) +
                              '] HH:mm',
                            nextDay:
                              '[' +
                              t('tomorrow', {
                                ns: 'sakiuiWeather',
                              }) +
                              '] HH:mm',
                            nextWeek: 'dddd',
                            lastDay:
                              '[' +
                              t('yesterday', {
                                ns: 'sakiuiWeather',
                              }) +
                              '] HH:mm',
                            lastWeek: 'YYYY-MM-DD HH:mm',
                            sameElse: 'YYYY-MM-DD HH:mm',
                          })}
                        </span>
                      </div>
                      <div className="item-l-bottom">
                        {v.uploadUser?.nickname ? (
                          <>
                            <SakiAvatar
                              onMarkClick={() => {
                                console.log('onMarkClick')
                              }}
                              width="20px"
                              height="20px"
                              border-radius="50%"
                              default-icon={'UserLine'}
                              nickname={v.uploadUser?.nickname || ''}
                              src={v.uploadUser?.avatar || ''}
                            ></SakiAvatar>
                            <span>{v.uploadUser?.nickname}</span>
                          </>
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                    <div className="item-right">
                      {i === 0 ? (
                        <span
                          style={{
                            color: '#999',
                            fontSize: '12px',
                          }}
                        >
                          {t('currentVersion', {
                            ns: 'historicalVersionPage',
                          })}
                        </span>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </SakiCheckboxItem>
              )
            })}
          </SakiCheckbox>
          <SakiScrollLoading
            onTap={() => {
              getHistoryFiles(pageNum)
            }}
            language={config.lang}
            type={loadStatus.current as any}
          ></SakiScrollLoading>
        </div>
      </div>
    </div>
  )
}

export default HistoricalVersionPage
